import React, { useState, useCallback } from 'react';
import { Cluster } from '../hooks/useClusters';
import { useClusterShops } from '../hooks/useClusterShops';
import ClusterShopsList from './ClusterShopsList';
import ClusterShopsMap from './ClusterShopsMap';
import ClusterShopSearch, { SearchResult } from './ClusterShopSearch';
import AddCoffeeShopDialog from '../../coffee_card_web/AddCoffeeForm/components/AddCoffeeShopDialog';
import { ClusterShop } from '../hooks/useClusterShops';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudDownloadAlt, faSyncAlt } from '@fortawesome/free-solid-svg-icons';

interface FetchEstimate {
    cells: number;
    strategiesPerCell: number;
    totalRequests: number;
    maxRequests: number;
}

interface ClusterShopsTabProps {
    selectedCluster: Cluster | null;
    clusters: Cluster[];
    onSelectCluster: (cluster: Cluster) => void;
    onClearCluster?: () => void;
    selectedClusterId: string | null;
    onFetch?: (id: string) => Promise<void>;
    onRefresh?: (id: string) => Promise<void>;
    onFetchEstimate?: (id: string) => Promise<FetchEstimate>;
    onSearchPlaces?: (id: string, query: string) => Promise<{ results: SearchResult[] }>;
    onAddPlaces?: (id: string, places: Array<{ place_id: string; name: string; lat: number; lng: number; address?: string; types?: string[] }>) => Promise<{ added: number; skipped: number }>;
    fetchingId?: string | null;
}

const ClusterShopsTab: React.FC<ClusterShopsTabProps> = ({
    selectedCluster,
    clusters,
    onSelectCluster,
    onClearCluster,
    selectedClusterId,
    onFetch,
    onRefresh,
    onFetchEstimate,
    onSearchPlaces,
    onAddPlaces,
    fetchingId
}) => {
    const [addShopOpen, setAddShopOpen] = useState(false);
    const [editingShopFromMap, setEditingShopFromMap] = useState<ClusterShop | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmEstimate, setConfirmEstimate] = useState<FetchEstimate | null>(null);
    const [confirmAction, setConfirmAction] = useState<'fetch' | 'refresh' | null>(null);

    const { shops, loading, refetch } = useClusterShops(selectedClusterId, true);
    const isFetching = !!fetchingId && fetchingId === selectedClusterId;

    const openConfirm = useCallback(async (action: 'fetch' | 'refresh') => {
        if (!selectedClusterId || !onFetchEstimate) return;
        try {
            const est = await onFetchEstimate(selectedClusterId);
            setConfirmEstimate(est);
            setConfirmAction(action);
            setConfirmOpen(true);
        } catch {
            // fallback: proceed without confirmation if estimate fails
            if (action === 'fetch' && onFetch) await onFetch(selectedClusterId);
            if (action === 'refresh' && onRefresh) await onRefresh(selectedClusterId);
            refetch();
        }
    }, [selectedClusterId, onFetchEstimate, onFetch, onRefresh, refetch]);

    const handleConfirm = useCallback(async () => {
        if (!selectedClusterId || !confirmAction) return;
        setConfirmOpen(false);
        const action = confirmAction;
        setConfirmEstimate(null);
        setConfirmAction(null);
        try {
            if (action === 'fetch' && onFetch) await onFetch(selectedClusterId);
            if (action === 'refresh' && onRefresh) await onRefresh(selectedClusterId);
            refetch();
        } catch {
            // error handled by parent
        }
    }, [selectedClusterId, confirmAction, onFetch, onRefresh, refetch]);

    const handleFetch = useCallback(() => {
        if (!selectedClusterId || !onFetch) return;
        if (onFetchEstimate) {
            openConfirm('fetch');
        } else {
            onFetch(selectedClusterId).then(refetch);
        }
    }, [selectedClusterId, onFetch, onFetchEstimate, openConfirm, refetch]);

    const handleRefresh = useCallback(() => {
        if (!selectedClusterId || !onRefresh) return;
        if (onFetchEstimate) {
            openConfirm('refresh');
        } else {
            onRefresh(selectedClusterId).then(refetch);
        }
    }, [selectedClusterId, onRefresh, onFetchEstimate, openConfirm, refetch]);

    const clusterCenter = selectedCluster?.center;
    const lat = clusterCenter?.lat ?? 40.6401;
    const lng = clusterCenter?.lng ?? 22.9444;

    const handleShopAdded = () => {
        refetch();
    };

    if (!selectedCluster) {
        return (
            <div className="p-8 text-center text-muted-foreground">
                <p className="mb-4">Select a cluster to view and manage its shops.</p>
                <div className="space-y-2 max-w-md mx-auto text-left">
                    {clusters.length === 0 ? (
                        <p className="text-sm">No clusters yet. Create one from the Map tab.</p>
                    ) : (
                        clusters.map((c) => (
                            <button
                                key={c._id}
                                type="button"
                                onClick={() => onSelectCluster(c)}
                                className="block w-full p-3 rounded-lg border border-ink/15 hover:bg-muted/30 text-left transition-colors"
                            >
                                <span className="font-medium text-ink">{c.name}</span>
                                <span className="text-sm ml-2">· {c.totalShops} shops · {c.city}, {c.country}</span>
                            </button>
                        ))
                    )}
                </div>
            </div>
        );
    }

    const lastFetched = selectedCluster?.lastFetchedAt
        ? new Date(selectedCluster.lastFetchedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })
        : 'Never';

    return (
        <div className="space-y-4">
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Google Places API usage</DialogTitle>
                        <DialogDescription>
                            {confirmEstimate && (
                                <span>
                                    This will make ~{confirmEstimate.maxRequests} API requests across {confirmEstimate.cells} cells
                                    with {confirmEstimate.strategiesPerCell} search strategies. Continue?
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirm}>
                            Continue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h3 className="font-semibold text-lg">{selectedCluster.name}</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        {selectedCluster.totalShops} shops · Last fetched: {lastFetched}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {onClearCluster && (
                        <button
                            type="button"
                            onClick={onClearCluster}
                            className="text-sm text-muted-foreground hover:text-ink px-2 py-1 rounded"
                        >
                            Change cluster
                        </button>
                    )}
                    {onFetch && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleFetch}
                            disabled={isFetching}
                            title="Fetch new shops from Google Places"
                        >
                            {isFetching ? (
                                <FontAwesomeIcon icon={faSyncAlt} className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <FontAwesomeIcon icon={faCloudDownloadAlt} className="mr-1.5 h-3.5 w-3.5" />
                            )}
                            Fetch from Google
                        </Button>
                    )}
                    {onRefresh && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleRefresh}
                            disabled={isFetching}
                            title="Refresh from Google Places (adds new, archives closed)"
                        >
                            {isFetching ? (
                                <FontAwesomeIcon icon={faSyncAlt} className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <FontAwesomeIcon icon={faSyncAlt} className="mr-1.5 h-3.5 w-3.5" />
                            )}
                            Refresh
                        </Button>
                    )}
                </div>
            </div>

            {selectedClusterId && onSearchPlaces && onAddPlaces && (
                <ClusterShopSearch
                    clusterId={selectedClusterId}
                    onSearch={onSearchPlaces}
                    onAddPlaces={onAddPlaces}
                    onRefetchShops={refetch}
                />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
                <ClusterShopsList
                    clusterId={selectedClusterId}
                    clusterName={selectedCluster.name}
                    clusterCenter={clusterCenter}
                    clusterCity={selectedCluster.city}
                    clusterCountry={selectedCluster.country}
                    shops={shops}
                    loading={loading}
                    onRefetch={refetch}
                    onAddShop={() => setAddShopOpen(true)}
                    editingShop={editingShopFromMap}
                    onEditShop={setEditingShopFromMap}
                />
            </div>
            <div>
                <ClusterShopsMap
                    clusterId={selectedClusterId}
                    cluster={selectedCluster}
                    shops={shops}
                    loading={loading}
                    onEditShop={setEditingShopFromMap}
                />
            </div>
            </div>

            <AddCoffeeShopDialog
                open={addShopOpen}
                onClose={() => setAddShopOpen(false)}
                onShopAdded={handleShopAdded}
                lat={lat}
                lng={lng}
                city={selectedCluster.city}
                country={selectedCluster.country}
                clusterId={selectedClusterId || undefined}
            />
        </div>
    );
};

export default ClusterShopsTab;
